import { BaseService } from "./base.service";
import { TableFilterModel } from "models/TableFilterModel";
import { ITableNumberColumnFilter } from "models/TableNumberColumnFilter";
import {
	IProcurementMethodType,
	IProcurementMethod,
	IProcurementMethodGridDetail,
	IProcurementMethodNumber,
	IProcurementMethodStatus,
	IProcurementMethodCategory,
} from "models/ProcurementMethod/procurementMethodModel";
import { IEvaluationMethodByThreshold } from "./../models/EvaluationMethod/evaluationMethodModel";
import { ProcurementMethodDetailModel } from "models/ProcurementMethod/procurementMethodDetailModel";
import { ProcurementMethodSelectionByAmount } from "models/Requisition/RequisitionProcurementMethodSelection";

class ProcurementMethodServiceInternal extends BaseService {
	getProcurementMethodList = async (): Promise<IProcurementMethod[]> =>
		this.callApi<IProcurementMethod[]>(`ProcurementMethod/GetAll`, "GET");

	getProcurementMethodGridList = async (
		model: TableFilterModel
	): Promise<IProcurementMethodGridDetail[]> =>
		this.callApi<IProcurementMethodGridDetail[]>(
			`ProcurementMethod/GetProcurementMethodList`,
			"POST",
			undefined,
			model
		);

	getProcurementMethodDetail = async (
		procurementId: number
	): Promise<ProcurementMethodDetailModel> =>
		this.callApi<ProcurementMethodDetailModel>(
			`ProcurementMethod/GetProcurementMethodData/${procurementId}`,
			"GET"
		);

	deleteProcurementMethod = async (procurementId: number): Promise<undefined> =>
		this.callApi<undefined>(
			`ProcurementMethod/Delete/${procurementId}`,
			"DELETE"
		);

	saveProcurementMethodDetail = async (
		model: ProcurementMethodDetailModel
	): Promise<{
		procurementMethodId: number;
		procurementMethodNumber: string;
	}> =>
		this.callApi<{
			procurementMethodId: number;
			procurementMethodNumber: string;
		}>(`ProcurementMethod/Save`, "POST", undefined, model);

	updateProcurementMethodDetail = async (
		model: ProcurementMethodDetailModel
	): Promise<{
		procurementMethodId: number;
		procurementMethodNumber: string;
	}> =>
		this.callApi<{
			procurementMethodId: number;
			procurementMethodNumber: string;
		}>(
			`ProcurementMethod/Update/${model.procurementMethodId}`,
			"PUT",
			undefined,
			model
		);

	getProcurementMethodNumberList = async (
		model: ITableNumberColumnFilter
	): Promise<IProcurementMethodNumber[]> =>
		this.callApi<IProcurementMethodNumber[]>(
			`ProcurementMethod/GetProcurementMethodNumberList`,
			"POST",
			undefined,
			model
		);

	getProcurementMethodTypeList = async (
		categoryIds: number[]
	): Promise<IProcurementMethodType[]> =>
		this.callApi<IProcurementMethodType[]>(
			`ProcurementMethodType/GetProcurementMethodTypeList`,
			"POST",
			undefined,
			categoryIds
		);

	getEvaluationMethodListFromThresholdToValue = async (
		thresholdTo: number
	): Promise<IEvaluationMethodByThreshold> =>
		this.callApi<IEvaluationMethodByThreshold>(
			`ProcurementMethod/GetEvaluationMethodListFromThresholdToValue/${thresholdTo}`,
			"GET"
		);

	getProcurementMethodStatus = async (): Promise<IProcurementMethodStatus[]> =>
		this.callApi<IProcurementMethodStatus[]>(
			`ProcurementMethodStatus/GetAll`,
			"GET"
		);

	checkProcurementMethodAlreadyUsed = async (
		procurementMethodId: number
	): Promise<{ isAlreadyUsed: boolean }> =>
		this.callApi<{ isAlreadyUsed: boolean }>(
			`ProcurementMethod/CheckProcurementMethodAlreadyUsed/${procurementMethodId}`,
			"GET"
		);

	updateProcurementMethodApproved = async (
		procurementMethodId: number
	): Promise<number> =>
		this.callApi<number>(
			`ProcurementMethod/UpdateProcurementMethodApproved/${procurementMethodId}`,
			"PUT"
		);

	getProcurementMethodSelectionList = async (
		selectedProcurementMethodId: number,
		totalAmount: number
	): Promise<ProcurementMethodSelectionByAmount[]> =>
		this.callApi<ProcurementMethodSelectionByAmount[]>(
			`ProcurementMethod/GetProcurementMethodSelectionList/${selectedProcurementMethodId}/${totalAmount}`,
			"GET"
		);

	getSbdDocumentsByProcurementMethodId = async (
		procurementMethodId: number
	): Promise<number[]> =>
		this.callApi<number[]>(
			`ProcurementMethodSbdDocument/GetSbdDocumentsByProcurementMethodId/${procurementMethodId}`,
			"GET"
		);

	getProcurementMethodCategoryList = async (): Promise<
		IProcurementMethodCategory[]
	> =>
		this.callApi<IProcurementMethodCategory[]>(
			`ProcurementMethodCategory/GetProcurementMethodCategoryList`,
			"GET"
		);

	getProcurementMethodListByBidTypeAndAmount = async (
		bidId: number
	): Promise<IProcurementMethod[]> =>
		this.callApi<IProcurementMethod[]>(
			`ProcurementMethod/GetProcurementMethodListByBidTypeAndAmount/${bidId}`,
			"GET"
		);
}

export const ProcurementMethodService = new ProcurementMethodServiceInternal();
